import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SubmitArrowButtonIcon from '@/components/Button/SubmitArrowButtonIcon';
import { InputField, PasswordInputField } from '@/components/InputFields';
import ClearIcon from '@/shared/assets/icons/clear-icon.svg?react';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import IconButton from '@/shared/components/IconButton';

import HiddenIdentifierInput from './HiddenIdentifierInput';
import TogglePassword from './TogglePassword';
import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly onSubmit: (password: string) => Promise<void>;
  readonly errorMessage?: string;
  readonly clearErrorMessage?: () => void;
  readonly appearance?: 'plain';
};

type FieldState = {
  newPassword: string;
  confirmPassword: string;
};

const SetPassword = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  appearance,
}: Props) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    watch,
    resetField,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FieldState>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  return (
    <form
      className={classNames(styles.form, appearance === 'plain' && styles.plain, className)}
      onSubmit={onSubmitHandler}
    >
      <HiddenIdentifierInput />
      {appearance === 'plain' ? (
        <>
          <PasswordInputField
            className={styles.inputField}
            appearance="plain"
            autoComplete="new-password"
            label={t('input.password')}
            placeholder={t('description.enter_password')}
            autoFocus={autoFocus}
            isDanger={!!errors.newPassword}
            errorMessage={errors.newPassword?.message}
            aria-invalid={!!errors.newPassword}
            {...register('newPassword', {
              required: t('error.password_required'),
            })}
          />
          <PasswordInputField
            className={styles.inputField}
            appearance="plain"
            autoComplete="new-password"
            label={t('input.confirm_password')}
            errorMessage={errors.confirmPassword?.message}
            aria-invalid={!!errors.confirmPassword}
            {...register('confirmPassword', {
              validate: (value) =>
                value === watch('newPassword') || t('error.passwords_do_not_match'),
            })}
          />
        </>
      ) : (
        <>
          <InputField
            className={styles.inputField}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            label={t('input.password')}
            autoFocus={autoFocus}
            isDanger={!!errors.newPassword}
            errorMessage={errors.newPassword?.message}
            aria-invalid={!!errors.newPassword}
            {...register('newPassword', {
              required: t('error.password_required'),
            })}
            isSuffixFocusVisible={!!watch('newPassword')}
            suffix={
              <IconButton
                onClick={() => {
                  resetField('newPassword');
                }}
              >
                <ClearIcon />
              </IconButton>
            }
          />

          <InputField
            className={styles.inputField}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            label={t('input.confirm_password')}
            errorMessage={errors.confirmPassword?.message}
            aria-invalid={!!errors.confirmPassword}
            {...register('confirmPassword', {
              validate: (value) =>
                value === watch('newPassword') || t('error.passwords_do_not_match'),
            })}
            isSuffixFocusVisible={!!watch('confirmPassword')}
            suffix={
              <IconButton
                onClick={() => {
                  resetField('confirmPassword');
                }}
              >
                <ClearIcon />
              </IconButton>
            }
          />

          <TogglePassword isChecked={showPassword} onChange={setShowPassword} />
        </>
      )}

      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}

      <Button
        name="submit"
        title="action.save_password"
        htmlType="submit"
        icon={appearance === 'plain' ? <SubmitArrowButtonIcon /> : undefined}
        iconPlacement={appearance === 'plain' ? 'edge' : 'inline'}
        isLoading={isSubmitting}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default SetPassword;
